'use client';

/**
 * Claim Profile Page — orchestrator only.
 *
 * State + side effects live in ./_hooks/useClaimInitiate.
 * Each UI state (loading / not-found / already-claimed / initiate / email-sent)
 * is delegated to a component — either the shared ../_shared/ClaimStatusView
 * (for plain icon+title+description layouts) or a co-located one in
 * ./_components for the claim-specific shapes.
 *
 * Epic: #917 Lawyer Profiles & Client-Lawyer Matching System
 * Issue: #923 Claim profile flow for lawyers
 */

import { AlertCircle, ArrowLeft, CheckCircle, Loader2 } from 'lucide-react';
import { useTranslations } from 'next-intl';
import { useParams, useSearchParams } from 'next/navigation';
import type { ReactElement } from 'react';

import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Link } from '@/i18n/routing';

import { ClaimStatusView } from '../_shared/ClaimStatusView';
import { ClaimEmailSentCard } from './_components/ClaimEmailSentCard';
import { ClaimMethodsBlock } from './_components/ClaimMethodsBlock';
import { ClaimPageHero } from './_components/ClaimPageHero';
import { ClaimThirdPartyNoticeCard } from './_components/ClaimThirdPartyNoticeCard';
import { ProfileBadgeCard } from './_components/ProfileBadgeCard';
import { useClaimInitiate } from './_hooks/useClaimInitiate';

export default function ClaimProfilePage(): ReactElement | null {
  const params = useParams();
  const searchParams = useSearchParams();
  const professionalId = params['id'] as string;
  const locale = (params['locale'] as string) || 'sk';
  // ``pending_inquiry_id`` is set when the lawyer arrived via a cold-
  // outreach email — threaded into the claim flow so on successful claim
  // they land on the inquiry-thread that brought them. Absent on organic
  // claims (lawyer found the page on their own).
  const pendingInquiryId = searchParams?.get('pending_inquiry_id') ?? null;
  const t = useTranslations('Lawyers.claim');

  const {
    professional,
    loadingProfessional,
    loadError,
    demandSignal,
    claimStatus,
    claimResponse,
    initiateError,
    triggerEmailClaim,
    declineAndNotifyThirdParty,
  } = useClaimInitiate(professionalId, pendingInquiryId);

  if (loadingProfessional) {
    return (
      <ClaimStatusView
        icon={Loader2}
        iconClassName='h-12 w-12 animate-spin text-brand-tertiary mb-4'
        title={`${t('loading')}...`}
      />
    );
  }

  if (loadError && !professional) {
    return (
      <ClaimStatusView
        icon={AlertCircle}
        iconClassName='h-12 w-12 text-status-error mb-4'
        title={loadError}
        action={
          <Button variant='outline' asChild>
            <Link href='/advokati'>
              <ArrowLeft className='h-4 w-4 mr-2' />
              {t('backToSearch')}
            </Link>
          </Button>
        }
      />
    );
  }

  if (professional?.is_claimed) {
    return (
      <ClaimStatusView
        icon={CheckCircle}
        iconClassName='h-12 w-12 text-status-success mb-4'
        title={t('alreadyClaimed')}
        description={t('alreadyClaimedDescription')}
        action={
          <Button variant='outline' asChild>
            <Link
              href={{
                pathname: '/advokati/[id]',
                params: { id: professionalId },
              }}
            >
              {t('viewProfile')}
            </Link>
          </Button>
        }
      />
    );
  }

  // Show the email-sent card during a follow-up resend too, so the page
  // doesn't flicker back to the initiate view between click and re-success.
  if (
    claimResponse &&
    (claimStatus === 'success' || claimStatus === 'loading')
  ) {
    return (
      <ClaimEmailSentCard
        maskedEmail={claimResponse.masked_email}
        professionalId={professionalId}
        expiresHours={claimResponse.expires_hours ?? null}
        onResend={triggerEmailClaim}
        isResending={claimStatus === 'loading'}
      />
    );
  }

  // Self-declaration gate (#8306): the visitor confirmed they are NOT the
  // professional — no email was sent. Never falls back to the generic
  // initiate view, which would let them retry and re-declare indefinitely.
  if (claimStatus === 'third_party' && professional) {
    return (
      <ClaimThirdPartyNoticeCard
        professionalId={professionalId}
        professionalName={professional.name}
        professionalEmail={professional.email ?? null}
      />
    );
  }

  if (!professional) return null;

  return (
    <main className='min-h-[80vh] px-4 py-10 sm:px-6 lg:px-8'>
      <div className='mx-auto w-full max-w-xl'>
        <div className='mb-4'>
          <Link
            href={{
              pathname: '/advokati/[id]',
              params: { id: professionalId },
            }}
            locale={locale}
            className='inline-flex items-center gap-1.5 text-sm text-content-secondary hover:text-brand-tertiary transition-colors'
          >
            <ArrowLeft className='h-4 w-4' />
            {t('backToProfile')}
          </Link>
        </div>

        {/* Hero (icon + title + subtitle + trust pills) lives OUTSIDE the card
            so the visual "weight" lands at the top of the page, not compressed
            inside a card header. Card below holds the two concrete blocks:
            profile confirmation + action. */}
        <ClaimPageHero demandSignal={demandSignal} />

        <Card className='border-line-default bg-surface-subtle shadow-sm overflow-hidden'>
          <div className='h-1 bg-gradient-to-r from-brand-tertiary via-status-info to-brand-tertiary' />

          <div className='p-6 space-y-5'>
            <ProfileBadgeCard
              professional={professional}
              email={professional.email ?? null}
            />
            <ClaimMethodsBlock
              hasEmail={Boolean(professional.has_email)}
              professionalName={professional.name}
              isLoading={claimStatus === 'loading'}
              error={claimStatus === 'error' ? initiateError : null}
              onVerifyViaEmail={triggerEmailClaim}
              onDeclineSelf={declineAndNotifyThirdParty}
            />
          </div>
        </Card>
      </div>
    </main>
  );
}
